<main>
  @if (isLoading > 0) {
    <loading-overlay [isLoading]="true"></loading-overlay>
  }

  <header>
    <div class="headline">
      <h1>{{ 'Reassign_Rolls_Title' | translateWF: 'Roll Reassignment' }}</h1>
    </div>
    <div class="buttons">
      <p>{{ 'Reassign_Rolls_Subtitle' | translateWF: 'Transfer rolls between production cards with validation' }}</p>
    </div>
  </header>

  <div class="content">
    <form [formGroup]="transferForm">
      <!-- Origin and Target Selection -->
      <p-panel [header]="'Reassign_Rolls_SelectOriginAndTarget' | translateWF: 'Select Origin and Target'">
        <!-- Origin Row -->
        <p-panel data-tone="surface" [header]="'Reassign_Rolls_Origin' | translateWF: 'Origin'">
          <p-card>
            <div class="selection-row">
              <div class="row-fields">
                <div class="form-field">
                  <label for="sourceWorkOrder">
                    {{ 'Reassign_Rolls_WorkOrder' | translateWF: 'Work Order' }}
                    <span class="required">*</span>
                  </label>
                  <p-autoComplete
                    formControlName="sourceWorkOrder"
                    #sourceWorkOrderSearch="cslServerSearch"
                    [cslServerSearch]="fetchSourceWorkOrders"
                    [suggestions]="sourceWorkOrderSearch.results()"
                    (onSelect)="onSourceWorkOrderSelected($event)"
                    (onClear)="clearSourceWorkOrder()"
                    [dropdown]="true"
                    [forceSelection]="true"
                    [showClear]="true"
                    placeholder="{{ 'Reassign_Rolls_SearchWorkOrder' | translateWF: 'Search work order...' }}"
                    inputId="sourceWorkOrder"
                    styleClass="w-full"
                    optionLabel="orderNumber"
                  >
                    <ng-template let-workOrder pTemplate="item">
                      <div>{{ workOrder.orderNumber }}</div>
                    </ng-template>
                  </p-autoComplete>
                </div>

                @if (transferForm.get('sourceWorkOrder')?.value) {
                  <div class="form-field">
                    <label for="originProductionCard">
                      {{ 'Reassign_Rolls_ProductionCard' | translateWF: 'Production Card' }}
                      <span class="required">*</span>
                    </label>
                    @if (originProductionCardSelectOptions().length > 0) {
                      <p-select
                        [options]="originProductionCardSelectOptions()"
                        formControlName="originProductionCard"
                        (onChange)="onOriginProductionCardSelected($event.value)"
                        [showClear]="true"
                        placeholder="{{ 'Reassign_Rolls_SelectProductionCard' | translateWF: 'Select a production card' }}"
                        styleClass="w-full"
                      />
                    } @else {
                      <div class="warning-message">
                        <i class="pi pi-exclamation-triangle"></i>
                        <span>{{ 'Reassign_Rolls_NoProductionCards' | translateWF: 'No production cards found' }}</span>
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          </p-card>
        </p-panel>

        <!-- Target Row -->
        @if (transferForm.get('originProductionCard')?.value) {
          <p-panel data-tone="surface" [header]="'Reassign_Rolls_Target' | translateWF: 'Target'">
            <p-card>
              <div class="selection-row">
                <div class="row-fields">
                  <div class="form-field">
                    <label for="targetWorkOrder">
                      {{ 'Reassign_Rolls_WorkOrder' | translateWF: 'Work Order' }}
                      <span class="required">*</span>
                    </label>
                    <p-autoComplete
                      formControlName="targetWorkOrder"
                      #targetWorkOrderSearch="cslServerSearch"
                      [cslServerSearch]="fetchTargetWorkOrders"
                      [suggestions]="targetWorkOrderSearch.results()"
                      (onSelect)="onTargetWorkOrderSelected($event)"
                      (onClear)="clearTargetWorkOrder()"
                      optionLabel="orderNumber"
                      [dropdown]="true"
                      [forceSelection]="true"
                      [showClear]="true"
                      placeholder="{{ 'Reassign_Rolls_SearchWorkOrder' | translateWF: 'Search work order...' }}"
                      inputId="targetWorkOrder"
                      styleClass="w-full"
                    >
                      <ng-template let-workOrder pTemplate="item">
                        <div>{{ workOrder.orderNumber }}</div>
                      </ng-template>
                    </p-autoComplete>
                  </div>

                  @if (transferForm.get('targetWorkOrder')?.value) {
                    <div class="form-field">
                      <label for="targetProductionCard">
                        {{ 'Reassign_Rolls_ProductionCard' | translateWF: 'Production Card' }}
                        <span class="required">*</span>
                      </label>
                      @if (targetProductionCardSelectOptions().length > 0) {
                        <p-select
                          [options]="targetProductionCardSelectOptions()"
                          formControlName="targetProductionCard"
                          (onChange)="onTargetProductionCardSelected($event.value)"
                          [showClear]="true"
                          placeholder="{{ 'Reassign_Rolls_SelectProductionCard' | translateWF: 'Select a production card' }}"
                          styleClass="w-full"
                        />
                      } @else {
                        <div class="warning-message">
                          <i class="pi pi-exclamation-triangle"></i>
                          <span>{{ 'Reassign_Rolls_NoProductionCards' | translateWF: 'No production cards found' }}</span>
                        </div>
                      }
                    </div>
                  }
                </div>
              </div>
            </p-card>
          </p-panel>
        }

        <!-- Check Transfer Button -->
        @if (transferForm.get('targetProductionCard')?.value) {
          <div class="action-buttons">
            <p-button
              [label]="'Reassign_Rolls_CheckTransfer' | translateWF: 'Check Transfer Compatibility'"
              icon="pi pi-search"
              [disabled]="transferForm.invalid"
              [loading]="isLoading > 0"
              (onClick)="checkTransfer()"
              severity="info"
            />
          </div>
        }
      </p-panel>
    </form>

    <!-- Step 2: Display Rolls Table (only after transfer check) -->
    @if (showTable()) {
      <p-panel [header]="'Reassign_Rolls_SelectRolls' | translateWF: 'Select Rolls to Transfer'">
        <p-card data-variant="compact">
          <!-- Single Validation Warning (only if there are issues) -->
          @if (!transferCheckResult()?.colorReferenceMatches || !transferCheckResult()?.fabricCodesCompatible) {
            <div class="warning-message">
              <i class="pi pi-exclamation-triangle"></i>
              <div>
                @if (!transferCheckResult()?.colorReferenceMatches) {
                  <strong>{{ 'Reassign_Rolls_ColorMismatch' | translateWF: 'Color references do not match.' }}</strong>
                }
                @if (!transferCheckResult()?.fabricCodesCompatible) {
                  <strong>{{ 'Reassign_Rolls_FabricIncompatible' | translateWF: 'Fabric codes incompatible:' }}</strong>
                  {{ transferCheckResult()!.incompatibleFabricCodes!.join(', ') }}
                  ({{ 'Reassign_Rolls_TargetAccepts' | translateWF: 'Target accepts:' }} {{ transferCheckResult()?.targetFabricCodes?.join(', ') }})
                }
              </div>
            </div>
          }

          @if (disabledRollNumbers().length > 0) {
            <div class="warning-message">
              <i class="pi pi-exclamation-triangle"></i>
              <span>
                {{ disabledRollNumbers().length }}
                {{ 'Reassign_Rolls_FinishedGoodsWarning' | translateWF: 'roll(s) are already in Finished Goods and cannot be reassigned. They are shown but disabled below.' }}
              </span>
            </div>
          }

          @if (rolls().length > 0) {
            <div class="info-card">
              <i class="pi pi-info-circle"></i>
              <span>
                {{ 'Reassign_Rolls_FoundRolls' | translateWF: 'Found' }} {{ rolls().length }}
                {{ 'Reassign_Rolls_RollsSelectThem' | translateWF: 'roll(s). Select the rolls you want to reassign.' }}
              </span>
            </div>

            <!-- Filters -->
            <div class="filters-panel">
              <div class="form-field">
                <label for="filterRollCategory">{{ 'Reassign_Rolls_FilterRollCategory' | translateWF: 'Roll Category' }}</label>
                <p-select
                  inputId="filterRollCategory"
                  [options]="rollCategoryFilterOptions"
                  optionLabel="label"
                  optionValue="value"
                  [ngModel]="filters().rollCategory"
                  (ngModelChange)="updateFilter({ rollCategory: $event })"
                  [ngModelOptions]="{ standalone: true }"
                  [showClear]="true"
                  placeholder="{{ 'Reassign_Rolls_FilterAll' | translateWF: 'All' }}"
                  styleClass="w-full"
                />
              </div>
              <div class="form-field">
                <label for="filterRollNumber">{{ 'Reassign_Rolls_FilterRollNumber' | translateWF: 'Roll Number' }}</label>
                <input
                  id="filterRollNumber"
                  type="text"
                  pInputText
                  class="w-full"
                  [ngModel]="filters().rollNumber"
                  (ngModelChange)="updateFilter({ rollNumber: $event })"
                  [ngModelOptions]="{ standalone: true }"
                  placeholder="{{ 'Reassign_Rolls_FilterSearch' | translateWF: 'Search...' }}"
                />
              </div>
              <div class="form-field">
                <label for="filterFabric">{{ 'Reassign_Rolls_FilterFabric' | translateWF: 'Fabric' }}</label>
                <p-select
                  inputId="filterFabric"
                  [options]="fabricFilterOptions()"
                  [ngModel]="filters().fabric"
                  (ngModelChange)="updateFilter({ fabric: $event })"
                  [ngModelOptions]="{ standalone: true }"
                  [showClear]="true"
                  placeholder="{{ 'Reassign_Rolls_FilterAll' | translateWF: 'All' }}"
                  styleClass="w-full"
                />
              </div>
              <div class="form-field">
                <label for="filterQuality">{{ 'Reassign_Rolls_FilterQuality' | translateWF: 'Quality' }}</label>
                <p-select
                  inputId="filterQuality"
                  [options]="rollQualityFilterOptions"
                  optionLabel="label"
                  optionValue="value"
                  [ngModel]="filters().quality"
                  (ngModelChange)="updateFilter({ quality: $event })"
                  [ngModelOptions]="{ standalone: true }"
                  [showClear]="true"
                  placeholder="{{ 'Reassign_Rolls_FilterAll' | translateWF: 'All' }}"
                  styleClass="w-full"
                />
              </div>
              <div class="form-field">
                <label for="filterLocation">{{ 'Reassign_Rolls_FilterLocation' | translateWF: 'Location' }}</label>
                <p-select
                  inputId="filterLocation"
                  [options]="locationFilterOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [ngModel]="filters().location"
                  (ngModelChange)="updateFilter({ location: $event })"
                  [ngModelOptions]="{ standalone: true }"
                  [showClear]="true"
                  placeholder="{{ 'Reassign_Rolls_FilterAll' | translateWF: 'All' }}"
                  styleClass="w-full"
                />
              </div>
              <div class="form-field filters-clear">
                <p-button
                  [label]="'Reassign_Rolls_ClearFilters' | translateWF: 'Clear Filters'"
                  icon="pi pi-filter-slash"
                  [text]="true"
                  severity="secondary"
                  (onClick)="clearFilters()"
                />
              </div>
            </div>

            <!-- Rolls Table -->
            <app-rolls-display-table
              [rolls]="filteredRolls()"
              [selectedRolls]="selectedRolls()"
              [disabledRollNumbers]="disabledRollNumbers()"
              [disabledRollTooltip]="'Reassign_Rolls_FinishedGoodsTooltip' | translateWF: 'This roll is already in Finished Goods and cannot be reassigned'"
              (selectionChange)="onRollSelectionChange($event)"
              [viewMode]="'reprocess'"
            ></app-rolls-display-table>

            <!-- Reassignment Summary and Action -->
            @if (selectedRolls().length > 0) {
              <div class="reassignment-summary">
                <h3>{{ 'Reassign_Rolls_Summary' | translateWF: 'Reassignment Summary' }}</h3>
                <ul>
                  <li>
                    <strong>{{ 'Reassign_Rolls_RollsToReassign' | translateWF: 'Rolls to reassign:' }}</strong>
                    {{ selectedRolls().length }}
                  </li>
                  <li>
                    <strong>{{ 'Reassign_Rolls_OriginPC' | translateWF: 'Origin Production Card:' }}</strong>
                    {{ transferForm.get('originProductionCard')?.value?.productionCardNumber }}
                  </li>
                  <li>
                    <strong>{{ 'Reassign_Rolls_TargetWO' | translateWF: 'Target Work Order:' }}</strong>
                    {{ transferForm.get('targetWorkOrder')?.value?.orderNumber }}
                  </li>
                  <li>
                    <strong>{{ 'Reassign_Rolls_TargetPC' | translateWF: 'Target Production Card:' }}</strong>
                    {{ transferForm.get('targetProductionCard')?.value?.productionCardNumber }}
                  </li>
                </ul>

                <div class="action-buttons">
                  <p-button
                    [label]="'Reassign_Rolls_ReassignButton' | translateWF: 'Reassign Rolls'"
                    icon="pi pi-check"
                    [disabled]="!canReassign()"
                    [loading]="isLoading > 0"
                    (onClick)="performReassignment()"
                    severity="success"
                  />
                </div>

                @if (!canReassign() && selectedRolls().length > 0) {
                  <div class="error-message">
                    <i class="pi pi-exclamation-circle"></i>
                    <span>
                      {{
                        'Reassign_Rolls_CannotReassign'
                          | translateWF
                            : 'Cannot reassign: validation checks must pass (color match
            and fabric compatibility)'
                      }}
                    </span>
                  </div>
                }
              </div>
            }
          } @else {
            <div class="warning-message">
              <i class="pi pi-info-circle"></i>
              <span>{{ 'Reassign_Rolls_NoRollsFound' | translateWF: 'No rolls found for the selected origin production card' }}</span>
            </div>
          }
        </p-card>
      </p-panel>
    }
  </div>
</main>
